import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from '@loader/ui';

import type { NangoIntegration } from '../hooks/useNangoConnectors';

type AuthKind = 'api_key' | 'basic';

export interface CredentialsResult {
  credentials: Record<string, string>;
  /** Stored as connectionConfig so sync scripts can read it */
  params: Record<string, string>;
}

interface CredentialsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  integration: NangoIntegration | null;
  authKind: AuthKind;
  loading: boolean;
  onSubmit: (integration: NangoIntegration, result: CredentialsResult) => void;
}

export function CredentialsDialog({
  open,
  onOpenChange,
  integration,
  authKind,
  loading,
  onSubmit,
}: CredentialsDialogProps) {
  const [baseUrl, setBaseUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!integration) return;

    const credentials: Record<string, string> = authKind === 'basic'
      ? { username, password }
      : { apiKey };

    onSubmit(integration, {
      credentials,
      params: baseUrl.trim() ? { base_url: baseUrl.trim().replace(/\/+$/, '') } : {},
    });
  };

  const handleOpenChange = (value: boolean) => {
    if (!value) {
      setBaseUrl('');
      setApiKey('');
      setUsername('');
      setPassword('');
    }
    onOpenChange(value);
  };

  const hasCredentials = authKind === 'basic'
    ? username.trim().length > 0 && password.trim().length > 0
    : apiKey.trim().length > 0;

  const isValid = hasCredentials && baseUrl.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            Connect {integration?.displayName ?? 'Integration'}
          </DialogTitle>
          <DialogDescription>
            Enter the API base URL and your credentials.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="cred-baseurl">Base URL</Label>
            <Input
              id="cred-baseurl"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="https://api.example.com"
              autoFocus
            />
          </div>

          {authKind === 'basic' ? (
            <>
              <div className="space-y-2">
                <Label htmlFor="cred-username">Username</Label>
                <Input
                  id="cred-username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Enter username"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cred-password">Password</Label>
                <Input
                  id="cred-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                />
              </div>
            </>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="cred-apikey">API Key</Label>
              <Input
                id="cred-apikey"
                type="password"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="Enter API key"
              />
            </div>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={!isValid || loading}>
              {loading ? 'Connecting...' : 'Connect'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
